import { useEffect, useState } from "react";
import { History, Loader2, Mail, RotateCcw, Save, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Modal } from "@/components/ui/modal";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/hooks/useToast";
import { useI18n } from "@/hooks/useI18n";
import { usePlatformFeeHistory } from "@/hooks/usePlatformFee";
import { PlatformFeeService } from "@/services/platformFee.service";
import { formatPlanDate } from "@/lib/builder/utils/payment-format";
import { formatPlatformFeeRate } from "@/lib/payments/utils/payment-method";
import type { PlatformFeeOwnerKind, PlatformFeeOwnerRow } from "@/integrations/supabase/rpc-types";
import { PLATFORM_FEE_SET_ERRORS, PlatformFeeRateBadge } from "@/lib/dashboard/agencies/platform-fee-display";

interface PlatformFeeOwnerSheetProps {
  kind: PlatformFeeOwnerKind;
  /** `null` chiude il pannello. */
  owner: PlatformFeeOwnerRow | null;
  defaultRate: number;
  onOpenChange: (open: boolean) => void;
  /** Dopo un salvataggio riuscito: la lista va ricaricata. */
  onSaved: () => void;
}

/** "2,5" o "2.5" → 0.025. null se non e' un numero fra 0 e 50 con al piu' due decimali. */
function parsePercentInput(value: string): number | null {
  const normalized = value.trim().replace(",", ".");
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(normalized)) return null;
  const percent = Number(normalized);
  if (!Number.isFinite(percent) || percent < 0 || percent > 50) return null;
  return Math.round(percent * 100) / 10000;
}

const toPercentInput = (rate: number) =>
  (rate * 100).toLocaleString("it-IT", { maximumFractionDigits: 2, useGrouping: false });

type PendingChange = { rate: number | null };

/**
 * Scheda dell'aliquota di un'agenzia o di un indipendente: valore attuale, modifica con
 * nota obbligatoria, conferma e storico. Una modifica vale per le proposte non ancora
 * inviate: quelle gia' uscite hanno l'aliquota congelata e non cambiano.
 */
export function PlatformFeeOwnerSheet({
  kind,
  owner,
  defaultRate,
  onOpenChange,
  onSaved,
}: PlatformFeeOwnerSheetProps) {
  const { t } = useI18n();
  const [rateInput, setRateInput] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [saving, setSaving] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const { data: history, loading: historyLoading } = usePlatformFeeHistory(owner?.id ?? null, historyKey);

  // Ogni apertura riparte dal valore attuale e da una nota vuota: la motivazione di una
  // trattativa non si eredita dalla precedente.
  useEffect(() => {
    if (owner) {
      setRateInput(toPercentInput(owner.rate));
      setNote("");
      setErrors(new Set());
      setPending(null);
    }
  }, [owner?.id]);

  if (!owner) return <Sheet open={false} onOpenChange={onOpenChange} />;

  const isCustom = owner.custom_rate !== null;

  // Anche il ritorno al default e' una trattativa che cambia: vuole la sua nota.
  const requestReset = () => {
    const next = new Set<string>(note.trim() ? [] : ["note"]);
    setErrors(next);
    if (next.size > 0) return;
    setPending({ rate: null });
  };

  const handleSubmit = () => {
    const rate = parsePercentInput(rateInput);
    const next = new Set<string>();
    if (rate === null) next.add("rate");
    if (!note.trim()) next.add("note");
    setErrors(next);
    if (next.size > 0) return;
    // Uguale al default = nessun accordo: lo stesso stato del "ripristina".
    setPending({ rate: rate === defaultRate ? null : rate });
  };

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const result = await PlatformFeeService.setRate(owner.id, pending.rate, note.trim());
      toast({
        title: result.changed
          ? t("dashboard.overview.agencies.saved", { rate: formatPlatformFeeRate(result.rate) })
          : t("dashboard.overview.agencies.unchanged"),
      });
      setPending(null);
      setNote("");
      setHistoryKey((key) => key + 1);
      onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = (Object.keys(PLATFORM_FEE_SET_ERRORS) as (keyof typeof PLATFORM_FEE_SET_ERRORS)[])
        .find((key) => message.includes(key));
      console.error("admin_set_platform_fee_rate error:", err);
      toast({
        title: t("common.error"),
        description: code ? t(PLATFORM_FEE_SET_ERRORS[code]) : message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const pendingRate = pending ? pending.rate ?? defaultRate : null;

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl w-full overflow-y-auto p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-gray-100 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle className="text-left truncate">{owner.name}</SheetTitle>
            <PlatformFeeRateBadge rate={owner.rate} isCustom={isCustom} />
          </div>
          <SheetDescription asChild>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                {owner.email}
              </span>
              {kind === "agency" && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {t("dashboard.overview.agencies.membersCount", { count: owner.members_count })}
                </span>
              )}
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="px-5 py-4 flex flex-col gap-5">
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("dashboard.overview.agencies.editTitle")}
            </h3>
            <p className="text-sm text-gray-500">
              {t(kind === "agency"
                ? "dashboard.overview.agencies.editHintAgency"
                : "dashboard.overview.agencies.editHintIndependent",
                { rate: formatPlatformFeeRate(defaultRate) })}
            </p>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="platform-fee-rate">{t("dashboard.overview.agencies.rateLabel")}</Label>
              <div className="relative w-40">
                <Input
                  id="platform-fee-rate"
                  inputMode="decimal"
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  className="pr-8 tabular-nums"
                  hasError={errors.has("rate")}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">%</span>
              </div>
              {errors.has("rate") && (
                <p className="text-xs text-red-600">{t("dashboard.overview.agencies.errors.invalidRate")}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="platform-fee-note">{t("dashboard.overview.agencies.noteLabel")}</Label>
              <Textarea
                id="platform-fee-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("dashboard.overview.agencies.notePlaceholder")}
                rows={3}
                className={errors.has("note") ? "border-red-500" : undefined}
              />
              {errors.has("note") && (
                <p className="text-xs text-red-600">{t("dashboard.overview.agencies.errors.noteRequired")}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSubmit} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {t("dashboard.overview.agencies.save")}
              </Button>
              {isCustom && (
                <Button variant="outline" onClick={requestReset} disabled={saving}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t("dashboard.overview.agencies.reset", { rate: formatPlatformFeeRate(defaultRate) })}
                </Button>
              )}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <History className="h-4 w-4" />
              {t("dashboard.overview.agencies.historyTitle")}
            </h3>
            {historyLoading ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500">{t("dashboard.overview.agencies.historyEmpty")}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200">
                {history.map((entry) => (
                  <li key={entry.id} className="px-3 py-2 flex flex-col gap-0.5">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-gray-900 tabular-nums">
                        {formatPlatformFeeRate(entry.old_rate ?? defaultRate)} → {formatPlatformFeeRate(entry.new_rate ?? defaultRate)}
                        {entry.new_rate === null && (
                          <span className="ml-1 font-normal text-gray-500">({t("dashboard.overview.agencies.rateDefault")})</span>
                        )}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatPlanDate(entry.changed_at)}
                        {entry.changed_by_name ? ` · ${entry.changed_by_name}` : ""}
                      </span>
                    </div>
                    {entry.note && <p className="text-sm text-gray-600 whitespace-pre-line">{entry.note}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <Modal
          isOpen={pending !== null}
          onClose={() => !saving && setPending(null)}
          title={t("dashboard.overview.agencies.confirmTitle")}
          size="sm"
          footer={
            <>
              <Button variant="outline" onClick={() => setPending(null)} disabled={saving}>
                {t("common.cancel")}
              </Button>
              <Button onClick={confirm} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("common.confirm")}
              </Button>
            </>
          }
        >
          {pendingRate !== null && (
            <div className="flex flex-col gap-2 text-sm text-gray-700">
              <p>
                {t("dashboard.overview.agencies.confirmBody", {
                  name: owner.name,
                  from: formatPlatformFeeRate(owner.rate),
                  to: formatPlatformFeeRate(pendingRate),
                })}
              </p>
              <p className="text-gray-500">{t("dashboard.overview.agencies.confirmScope")}</p>
            </div>
          )}
        </Modal>
      </SheetContent>
    </Sheet>
  );
}

export default PlatformFeeOwnerSheet;
